<script setup>
import { toRefs, computed } from 'vue';
import { RouterLink } from 'vue-router';

const props = defineProps({
  item: {
    type: Object,
    required: true,
    default: () => ({
      id: 0,
      featuredImage: '',
      featuredImageAltText: '',
      title: '',
      tags: [],
      maxReadTime: 0,
      slug: '',
    }),
  },
  routePath: {
    type: String,
    required: true,
  },
  showReadTime: {
    type: Boolean,
    required: false,
    default: false,
  },
  activeFilter: {
    type: String,
    default: 'all',
  },
  readNowText: {
    type: String, 
    default: 'Read Now'
  }
});

const { item, routePath, activeFilter } = toRefs(props);

// Compute the tags to display: prioritize activeFilter if it exists in tags, then show up to 3 tags total
const displayedTags = computed(() => {
  if (!item.value.tags || item.value.tags.length === 0) return [];

  const tags = [...item.value.tags];
  const filteredTags = [];

  // If activeFilter is not 'all' and exists in tags, add it first
  if (activeFilter.value !== 'all' && tags.includes(activeFilter.value)) {
    filteredTags.push(activeFilter.value);
    // Remove the activeFilter from the tags array to avoid duplication
    tags.splice(tags.indexOf(activeFilter.value), 1);
  }

  // Add remaining tags up to a total of 3
  return filteredTags.concat(tags).slice(0, 3);
});
</script>

<template>
  <RouterLink :to="`${routePath}/${item.slug}`" class="card">
    <img
      :src="item.featuredImage"
      :alt="item.featuredImageAltText"
      class="card-image"
    />
    <div class="card-content">
      <div class="card-tags">
        <span v-for="tag in displayedTags" :key="tag" class="tag">{{ tag }}</span>
      </div>
      <h3 class="card-title clamped-text">{{ item.title }}</h3>
      <div class="card-meta">
        <button class="read-now">{{readNowText}}</button>
        <span class="read-time" v-if="showReadTime">
          <svg
            class="clock-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          Max {{ item.maxReadTime }} min read
        </span>
      </div>
    </div>
  </RouterLink>
</template>

<style scoped>
.card {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  max-width: 350px;
  min-width: 280px;
  margin: 15px;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.card-image {
  width: 100%;
  aspect-ratio: 1.77777;
  object-fit: cover;
}

.card-content {
  height: 210px;
  padding: 15px;
  text-align: left;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.card-tags {
  margin-bottom: 10px;
}

.tag {
  display: inline-block;
  background-color: #e0e7ff;
  color: #4b5eaa;
  padding: 4px 16px;
  border-radius: 12px;
  font-size: 0.8em;
  margin-right: 5px;
}

.card-title {
  font-size: 1.2em;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
  flex-grow: 1;
}

.clamped-text {
  -webkit-line-clamp: 3;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
}

.read-now {
  background-color: #007ab2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 0.9em;
  cursor: pointer;
  transition: background-color 0.3s;
}

.read-now:hover {
  opacity: 0.8;
}

.read-time {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.9em;
}

.clock-icon {
  margin-right: 5px;
}
</style>